import { SEO } from "@/components/SEO";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useLanguage } from "@/contexts/LanguageContext";
import {
  ArrowRight,
  ChevronDown,
  Check,
  Clock,
  Database,
  GraduationCap,
  Rocket,
  Settings,
  ShieldCheck,
} from "lucide-react";

const CANONICAL = "https://cargonova.ai/implementation";
const CANONICAL_ZH = "https://cargonova.ai/zh/implementation";
const CANONICAL_TH = "https://cargonova.ai/th/implementation";
const PUBLISHED = "2026-07-20";
const UPDATED = "2026-07-20";

const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "CargoNova Implementation & Migration: Go-Live in Weeks",
  description:
    "Learn how CargoNova is implemented and how freight forwarders migrate from CargoWise or legacy TMS with phased onboarding, data migration and training.",
  datePublished: PUBLISHED,
  dateModified: UPDATED,
  author: { "@type": "Organization", name: "CargoNova" },
  publisher: { "@id": "https://cargonova.ai/#organization" },
  mainEntityOfPage: CANONICAL,
  inLanguage: "en",
  about: [
    "Freight forwarding software implementation",
    "CargoWise migration",
    "TMS onboarding",
  ],
};

const contentEn = {
  eyebrow: "Implementation",
  title: "Go Live in Weeks,\nNot Years",
  summary:
    "CargoNova is designed for fast, low-risk onboarding. Most forwarders get their first office live in weeks using standard migration templates and phased rollout.",
  intro:
    "Whether you are replacing spreadsheets, a regional TMS or CargoWise, our team provides templates, integration patterns and training so you can modernize without business disruption.",

  phasesTitle: "The Implementation Journey",
  phases: [
    {
      icon: Rocket,
      step: "01",
      title: "Tenant setup",
      body: "Configure your environment, user roles, branches and security policies. Core workflows are mapped to your existing process language.",
    },
    {
      icon: Database,
      step: "02",
      title: "Data migration",
      body: "Extract and load customers, vendors, trade lanes, tariffs and open shipments. We provide templates to reduce manual mapping.",
    },
    {
      icon: Settings,
      step: "03",
      title: "Workflow alignment",
      body: "Connect AI agents for quoting, booking, documentation and settlement. Integrate with your FMS, TMS or ERP where needed.",
    },
    {
      icon: GraduationCap,
      step: "04",
      title: "Training & go-live",
      body: "Train operators and supervisors on the new cockpit, then go live with a pilot office or trade lane before broader rollout.",
    },
  ],

  migrationTitle: "Migrating from CargoWise or Legacy Systems",
  migration: [
    { title: "Audit current data", body: "Identify master data, open shipments and key workflows to migrate first." },
    { title: "Pilot lane or office", body: "Run CargoNova alongside the legacy system to validate flows and train the team." },
    { title: "Phased expansion", body: "Add branches, trade lanes and AI agents once the pilot is stable." },
    { title: "Decommission legacy", body: "Retire old screens and spreadsheets when teams are confident and data is clean." },
  ],

  timelineTitle: "Typical Timeline",
  timeline: [
    { phase: "Week 1–2", task: "Discovery, tenant setup and data template preparation" },
    { phase: "Week 3–4", task: "Data load, workflow configuration and integration wiring" },
    { phase: "Week 5–6", task: "User training, pilot go-live and exception handling" },
    { phase: "Week 7+", task: "Rollout to additional offices, lanes and agents" },
  ],

  benefitsTitle: "Why Phased Rollout Works",
  benefits: [
    { metric: "Low", title: "Business risk", body: "Keep legacy systems live during transition so operations continue uninterrupted." },
    { metric: "Fast", title: "Time to value", body: "Pilot offices often see productivity gains within the first month." },
    { metric: "High", title: "Team adoption", body: "Training happens in real workflows, making change management easier." },
  ],

  faqTitle: "Implementation Questions",
  faqs: [
    {
      q: "How long does implementation take?",
      a: "Most SME forwarders go live in a few weeks. Larger migrations from systems like CargoWise may take a few months depending on data complexity and scope.",
    },
    {
      q: "Will our operations stop during migration?",
      a: "No. We use a phased approach where the legacy system stays live until CargoNova is proven stable for your team.",
    },
    {
      q: "What data can be migrated?",
      a: "Customers, vendors, trade lanes, tariffs, contracts, open shipments and historical invoices can typically be migrated using our templates.",
    },
  ],

  ctaTitle: "Plan your migration",
  ctaBody:
    "Book a call with our implementation team to review your current systems and build a low-risk rollout plan.",
  ctaButton: "Book a consultation",
  ctaLink: "https://www.deep-insights.ai/book-demo",
  backLabel: "Back to Home",
  backTo: "/",
};

const contentZh = {
  eyebrow: "实施迁移",
  title: "数周上线，\n而非数年",
  summary:
    "CargoNova 专为快速、低风险上线而设计。借助标准迁移模板与分阶段推广，大多数货代企业可在数周内让首个办公室稳定运行。",
  intro:
    "无论您当前使用 Excel、区域 TMS 还是 CargoWise，我们的团队都会提供模板、集成方案与培训，帮助您在不影响业务的前提下完成现代化升级。",

  phasesTitle: "实施路径",
  phases: [
    {
      icon: Rocket,
      step: "01",
      title: "环境搭建",
      body: "配置租户环境、用户角色、分支机构和安全策略，将核心流程映射到您现有的作业语言。",
    },
    {
      icon: Database,
      step: "02",
      title: "数据迁移",
      body: "导出并导入客户、供应商、航线、费率和在途订单等主数据，我们提供模板以减少人工映射工作。",
    },
    {
      icon: Settings,
      step: "03",
      title: "流程对齐",
      body: "接入报价、订舱、单证、结算等 AI Agent，并按需与 FMS、TMS 或 ERP 完成集成。",
    },
    {
      icon: GraduationCap,
      step: "04",
      title: "培训与上线",
      body: "对操作与管理人员进行新系统培训，先以试点办公室或贸易航线上线，再逐步推广。",
    },
  ],

  migrationTitle: "从 CargoWise 或旧系统迁移",
  migration: [
    { title: "盘点现有数据", body: "识别需要优先迁移的主数据、在途订单和关键流程。" },
    { title: "试点航线或办公室", body: "让 CargoNova 与旧系统并行运行，验证流程并培训团队。" },
    { title: "分阶段扩展", body: "试点稳定后，再逐步扩展到更多分支、航线和 AI Agent。" },
    { title: "退役旧系统", body: "当团队熟练且数据干净后，逐步停用旧界面和 Excel。" },
  ],

  timelineTitle: "典型上线周期",
  timeline: [
    { phase: "第 1–2 周", task: "需求梳理、租户搭建与数据模板准备" },
    { phase: "第 3–4 周", task: "数据导入、流程配置与集成对接" },
    { phase: "第 5–6 周", task: "用户培训、试点上线与异常处理" },
    { phase: "第 7 周起", task: "推广至更多办公室、航线和 Agent" },
  ],

  benefitsTitle: "为什么分阶段上线更有效",
  benefits: [
    { metric: "低", title: "业务风险", body: "过渡期间保留旧系统并行运行，确保业务不中断。" },
    { metric: "快", title: "价值兑现", body: "试点办公室通常在首月即可感受到效率提升。" },
    { metric: "高", title: "团队接受度", body: "在真实工作流中培训，降低变革阻力。" },
  ],

  faqTitle: "实施常见问题",
  faqs: [
    {
      q: "实施周期一般多长？",
      a: "大多数中小型货代可在数周内上线。从 CargoWise 等复杂系统迁移可能需要数月，具体取决于数据复杂度和范围。",
    },
    {
      q: "迁移期间业务会中断吗？",
      a: "不会。我们采用分阶段策略，旧系统会保持运行，直到 CargoNova 在您的团队中被验证稳定。",
    },
    {
      q: "哪些数据可以迁移？",
      a: "客户、供应商、航线、费率、合约、在途订单和历史账单通常都可以通过我们的模板完成迁移。",
    },
  ],

  ctaTitle: "规划您的迁移方案",
  ctaBody:
    "预约与实施团队的沟通，我们会基于您现有系统制定一份低风险的上线推广计划。",
  ctaButton: "预约咨询",
  ctaLink: "https://www.deep-insights.ai/book-demo",
  backLabel: "返回首页",
  backTo: "/zh",
};

const contentTh = {
  eyebrow: "การวางระบบและย้ายข้อมูล",
  title: "เปิดใช้งานได้ในไม่กี่สัปดาห์\nไม่ใช่หลายปี",
  summary:
    "CargoNova ออกแบบมาเพื่อการเริ่มใช้งานที่รวดเร็วและความเสี่ยงต่ำ ฟอร์วาร์ดเดอร์ส่วนใหญ่สามารถเปิดใช้งานสำนักงานแรกได้ภายในไม่กี่สัปดาห์ ด้วยเทมเพลตการย้ายข้อมูลมาตรฐานและการทยอยเปิดใช้งานเป็นระยะ",
  intro:
    "ไม่ว่าคุณจะกำลังใช้ Excel ระบบ TMS ภายในภูมิภาค หรือ CargoWise ทีมของเรามีเทมเพลต รูปแบบการเชื่อมต่อระบบ และการอบรม เพื่อให้คุณยกระดับระบบได้โดยไม่กระทบต่อการดำเนินงาน",

  phasesTitle: "ขั้นตอนการวางระบบ",
  phases: [
    {
      icon: Rocket,
      step: "01",
      title: "ตั้งค่าสภาพแวดล้อม",
      body: "กำหนดค่าสภาพแวดล้อมองค์กร บทบาทผู้ใช้ สาขา และนโยบายความปลอดภัย พร้อมจับคู่กระบวนการหลักให้เข้ากับภาษาการทำงานเดิมของคุณ",
    },
    {
      icon: Database,
      step: "02",
      title: "ย้ายข้อมูล",
      body: "ดึงและนำเข้าข้อมูลลูกค้า คู่ค้า เส้นทางเดินเรือ ค่าระวาง และงานขนส่งที่กำลังดำเนินอยู่ พร้อมเทมเพลตที่ช่วยลดการจับคู่ข้อมูลด้วยมือ",
    },
    {
      icon: Settings,
      step: "03",
      title: "ปรับกระบวนการทำงาน",
      body: "เชื่อมต่อ AI Agent สำหรับการเสนอราคา จองระวาง เอกสาร และการชำระเงิน พร้อมเชื่อมต่อกับ FMS TMS หรือ ERP ตามความจำเป็น",
    },
    {
      icon: GraduationCap,
      step: "04",
      title: "อบรมและเปิดใช้งาน",
      body: "อบรมพนักงานปฏิบัติการและหัวหน้างานให้ใช้ระบบใหม่ แล้วเริ่มใช้งานจริงกับสำนักงานหรือเส้นทางนำร่องก่อนขยายในวงกว้าง",
    },
  ],

  migrationTitle: "การย้ายจาก CargoWise หรือระบบเดิม",
  migration: [
    { title: "ตรวจสอบข้อมูลปัจจุบัน", body: "ระบุข้อมูลหลัก งานขนส่งที่ค้างอยู่ และกระบวนการสำคัญที่ต้องย้ายก่อน" },
    { title: "นำร่องด้วยเส้นทางหรือสำนักงาน", body: "ใช้ CargoNova ควบคู่กับระบบเดิม เพื่อตรวจสอบกระบวนการและอบรมทีมงาน" },
    { title: "ขยายเป็นระยะ", body: "เพิ่มสาขา เส้นทางเดินเรือ และ AI Agent เมื่อระยะนำร่องเสถียร" },
    { title: "ปลดระวางระบบเดิม", body: "ยกเลิกหน้าจอเก่าและไฟล์ Excel เมื่อทีมงานมั่นใจและข้อมูลถูกต้องครบถ้วน" },
  ],

  timelineTitle: "ระยะเวลาโดยทั่วไป",
  timeline: [
    { phase: "สัปดาห์ที่ 1–2", task: "สำรวจความต้องการ ตั้งค่าสภาพแวดล้อม และเตรียมเทมเพลตข้อมูล" },
    { phase: "สัปดาห์ที่ 3–4", task: "นำเข้าข้อมูล ตั้งค่ากระบวนการทำงาน และเชื่อมต่อระบบ" },
    { phase: "สัปดาห์ที่ 5–6", task: "อบรมผู้ใช้งาน เปิดใช้งานนำร่อง และจัดการกรณีพิเศษ" },
    { phase: "สัปดาห์ที่ 7 เป็นต้นไป", task: "ขยายไปยังสำนักงาน เส้นทาง และ Agent เพิ่มเติม" },
  ],

  benefitsTitle: "ทำไมการทยอยเปิดใช้งานเป็นระยะจึงได้ผล",
  benefits: [
    { metric: "ต่ำ", title: "ความเสี่ยงต่อธุรกิจ", body: "ระบบเดิมยังคงใช้งานอยู่ระหว่างการเปลี่ยนผ่าน จึงไม่กระทบการดำเนินงาน" },
    { metric: "รวดเร็ว", title: "เห็นผลไว", body: "สำนักงานนำร่องมักเห็นผลด้านประสิทธิภาพภายในเดือนแรก" },
    { metric: "สูง", title: "ทีมงานยอมรับ", body: "อบรมภายในกระบวนการทำงานจริง ช่วยลดแรงต้านต่อการเปลี่ยนแปลง" },
  ],

  faqTitle: "คำถามที่พบบ่อยเรื่องการวางระบบ",
  faqs: [
    {
      q: "การวางระบบใช้เวลานานเท่าไร?",
      a: "ฟอร์วาร์ดเดอร์ขนาดกลางและเล็กส่วนใหญ่เปิดใช้งานได้ภายในไม่กี่สัปดาห์ การย้ายจากระบบซับซ้อนเช่น CargoWise อาจใช้เวลาหลายเดือน ขึ้นอยู่กับความซับซ้อนและขอบเขตของข้อมูล",
    },
    {
      q: "การดำเนินงานจะหยุดชะงักระหว่างย้ายข้อมูลหรือไม่?",
      a: "ไม่หยุด เราใช้แนวทางแบบทยอยเป็นระยะ โดยระบบเดิมยังใช้งานอยู่จนกว่า CargoNova จะพิสูจน์แล้วว่าเสถียรสำหรับทีมของคุณ",
    },
    {
      q: "ข้อมูลใดที่สามารถย้ายได้บ้าง?",
      a: "ลูกค้า คู่ค้า เส้นทางเดินเรือ ค่าระวาง สัญญา งานขนส่งที่ค้างอยู่ และประวัติใบแจ้งหนี้ สามารถย้ายได้ด้วยเทมเพลตของเรา",
    },
  ],

  ctaTitle: "วางแผนการย้ายระบบของคุณ",
  ctaBody:
    "นัดคุยกับทีมวางระบบของเรา เพื่อตรวจสอบระบบปัจจุบันและจัดทำแผนการเปิดใช้งานที่มีความเสี่ยงต่ำ",
  ctaButton: "นัดหมายปรึกษา",
  ctaLink: "https://www.deep-insights.ai/book-demo",
  backLabel: "กลับสู่หน้าแรก",
  backTo: "/th",
};

const Implementation = () => {
  const { language } = useLanguage();
  const isZh = language === "zh";
  const isTh = language === "th";
  const c = isZh ? contentZh : isTh ? contentTh : contentEn;

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: c.faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://cargonova.ai/" },
      { "@type": "ListItem", position: 2, name: "Implementation", item: CANONICAL },
    ],
  };

  const pageTitle = isZh
    ? "CargoNova 实施与迁移 | 数周上线指南"
    : isTh
    ? "CargoNova Implementation | แผน go-live แบบ phased rollout"
    : "CargoNova Implementation & Migration | Go-Live in Weeks";

  const pageDescription = isZh
    ? "了解 CargoNova 的实施路径、从 CargoWise 或旧系统迁移的方法，以及分阶段上线如何降低风险。"
    : isTh
    ? "เรียนรู้ journey การ implement CargoNova วิธี migrate จาก CargoWise หรือ legacy systems และการลดความเสี่ยงด้วย phased rollout"
    : "Learn how CargoNova is implemented, how to migrate from CargoWise or legacy TMS, and why phased rollout reduces risk.";

  const pageCanonical = isZh ? CANONICAL_ZH : isTh ? CANONICAL_TH : CANONICAL;

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title={pageTitle}
        description={pageDescription}
        canonical={CANONICAL}
        canonicalZh={CANONICAL_ZH}
        canonicalTh={CANONICAL_TH}
        jsonLd={[articleSchema, faqSchema, breadcrumbSchema]}
      />

      <Navbar />

      <main className="relative pt-24 pb-20 overflow-hidden bg-gradient-to-tr from-secondary/10 via-background to-accent/10">
        <div className="absolute -top-20 -right-20 w-[36rem] h-[36rem] bg-secondary/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 -left-32 w-[32rem] h-[32rem] bg-accent/15 rounded-full blur-3xl pointer-events-none" />

        {/* Hero */}
        <section className="relative container mx-auto px-4 max-w-5xl pt-6 md:pt-10 pb-5 md:pb-8">
          <span className="inline-block py-1 px-2.5 rounded-full bg-secondary/10 text-secondary text-[11px] font-semibold tracking-wider uppercase mb-4 ring-1 ring-secondary/20">
            {c.eyebrow}
          </span>
          <h1
            className={`text-2xl md:text-3xl lg:text-4xl font-bold tracking-tight mb-3 ${
              isZh ? "leading-[1.5159375]" : "leading-[1.25]"
            } bg-gradient-to-r from-foreground via-secondary to-foreground bg-clip-text text-transparent whitespace-pre-line`}
          >
            {c.title}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-3">
            {c.summary}
          </p>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
            {c.intro}
          </p>
        </section>

        {/* Phases */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-6">
            {c.phasesTitle}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {c.phases.map((p) => {
              const Icon = p.icon;
              return (
                <div
                  key={p.step}
                  className="group relative overflow-hidden rounded-2xl bg-card ring-1 ring-border/60 shadow-xs hover:shadow-md transition-all duration-300 p-6"
                >
                  <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-secondary to-accent" />
                  <div className="flex items-center gap-3 mb-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary/10 text-secondary">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="text-xs font-bold text-secondary uppercase tracking-wider">
                      {p.step}
                    </span>
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2 leading-snug">
                    {p.title}
                  </h3>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {p.body}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        {/* Migration path */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-6">
            {c.migrationTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {c.migration.map((m, idx) => (
              <div
                key={idx}
                className="group relative overflow-hidden rounded-2xl bg-card ring-1 ring-border/60 shadow-xs hover:shadow-md transition-all duration-300 p-6"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-secondary to-accent" />
                <div className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary/10 text-secondary mt-0.5">
                    <Check className="h-3.5 w-3.5" />
                  </span>
                  <div>
                    <h3 className="text-base font-semibold text-foreground mb-2 leading-snug">
                      {m.title}
                    </h3>
                    <p className="text-sm leading-relaxed text-muted-foreground">
                      {m.body}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Timeline */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-xl md:text-2xl font-bold text-foreground mb-6">
            {c.timelineTitle}
          </h2>
          <div className="relative pl-6 md:pl-8 border-l-2 border-secondary/20 space-y-6">
            {c.timeline.map((t, idx) => (
              <div key={idx} className="relative">
                <span className="absolute -left-[31px] md:-left-[39px] top-1 flex h-5 w-5 items-center justify-center rounded-full bg-secondary/20 ring-4 ring-background">
                  <Clock className="h-2.5 w-2.5 text-secondary" />
                </span>
                <h3 className="text-base font-semibold text-foreground mb-1">
                  {t.phase}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {t.task}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Benefits */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
            {c.benefitsTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {c.benefits.map((b) => (
              <div
                key={b.title}
                className="group relative overflow-hidden rounded-2xl bg-card ring-1 ring-border/60 shadow-xs hover:shadow-md transition-all duration-300 p-6"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-secondary to-accent" />
                <div className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-secondary to-accent bg-clip-text text-transparent mb-2 leading-none">
                  {b.metric}
                </div>
                <h3 className="text-base md:text-lg font-semibold text-foreground mb-2 leading-snug">
                  {b.title}
                </h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {b.body}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ */}
        <section className="relative container mx-auto px-4 max-w-5xl py-8 md:py-12">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
            {c.faqTitle}
          </h2>
          <div className="flex flex-col gap-3">
            {c.faqs.map((f) => (
              <details
                key={f.q}
                className="group rounded-2xl bg-card/80 backdrop-blur ring-1 ring-border/50 p-5 open:ring-secondary/50"
              >
                <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
                  <span className="text-base font-semibold text-foreground">
                    {f.q}
                  </span>
                  <ChevronDown className="h-4 w-4 text-secondary transition-transform group-open:rotate-180" />
                </summary>
                <p className="mt-3 text-sm md:text-[15px] leading-relaxed text-muted-foreground">
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="relative container mx-auto px-4 max-w-3xl py-8">
          <div className="rounded-3xl bg-gradient-to-br from-secondary/15 via-card to-accent/10 ring-1 ring-secondary/30 p-6 md:p-8 shadow-[0_20px_60px_-24px_hsl(var(--secondary)/0.35)]">
            <h3 className="text-lg md:text-xl font-bold text-foreground mb-2">
              {c.ctaTitle}
            </h3>
            <p className="text-sm md:text-base text-muted-foreground mb-5">
              {c.ctaBody}
            </p>
            <a
              href={c.ctaLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-secondary to-accent px-6 py-3 text-sm font-semibold text-secondary-foreground shadow-lg shadow-secondary/30 transition-transform hover:-translate-y-0.5"
            >
              {c.ctaButton}
              <ArrowRight className="h-4 w-4" />
            </a>
            <div className="mt-5">
              <a
                href={c.backTo}
                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-secondary transition-colors"
              >
                <ArrowRight className="h-3.5 w-3.5 rotate-180" />
                {c.backLabel}
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Implementation;
